/**
 * Forgot Password Route
 * Request a password-reset email. Posts to the auth API's
 * /auth/password/reset-request endpoint (Better-Auth aware).
 */

import type { LoaderFunctionArgs, ActionFunctionArgs, MetaFunction } from '@remix-run/node';
import { redirect, json } from '@remix-run/node';
import { Form, Link, useActionData, useNavigation } from '@remix-run/react';
import { AuthLayout, Button, Input, Label } from '@yak/ui';

import loginImage from '~/assets/login_image.png';
import { isAuthenticated, requestPasswordReset } from '~/lib/auth.server';

export const meta: MetaFunction = () => {
  return [
    { title: 'Forgot Password - Yak Interactive' },
    { name: 'description', content: 'Reset your Yak Interactive password' },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  if (await isAuthenticated(request)) {
    return redirect('/home');
  }
  return json({});
}

interface ActionData {
  success?: boolean;
  error?: string;
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get('email')?.toString().trim();

  if (!email) {
    return json<ActionData>({ error: 'Email is required' }, { status: 400 });
  }

  try {
    await requestPasswordReset(email);
    // Generic confirmation regardless of whether the account exists,
    // to avoid leaking which emails are registered.
    return json<ActionData>({ success: true });
  } catch (err) {
    return json<ActionData>(
      { error: err instanceof Error ? err.message : 'Unable to send reset email. Please try again.' },
      { status: 400 }
    );
  }
}

export default function ForgotPassword() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="Enter your email and we'll send you a link to reset it."
      imageUrl={loginImage}
      imageAlt="Person using mobile device"
      imagePosition="right"
      footer={
        <p className="text-center text-sm text-muted-foreground">
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      }
    >
      {actionData?.success ? (
        <div
          className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-700"
          role="status"
        >
          If an account exists for that email, we&apos;ve sent password reset
          instructions. Please check your inbox.
        </div>
      ) : (
        <Form method="post" className="space-y-4">
          {actionData?.error && (
            <div
              className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
              role="alert"
            >
              {actionData.error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
            />
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send reset link'}
          </Button>
        </Form>
      )}
    </AuthLayout>
  );
}
